<script>
import { h } from 'vue';

import dropdown from '@/constants/elements/dropdown';
import emitter from '@/emitter';

const pointerSize = 4;
const directions = {
  left: [-1, 0],
  right: [1, 0],
  top: [0, 1],
  bottom: [0, -1],
  bottomsidebar: [0, -1],
};
export default {
  name: 'Popover',
  props: {
    name: {
      type: String,
      required: true,
    },
    top: {
      type: [Number, String],
    },
    width: {
      type: [Number, String],
      default: 180,
    },
    pointer: {
      type: Boolean,
      default: true,
    },
    event: {
      type: String,
      default: 'click',
    },
    anchor: {
      type: Number,
      default: 0.5,
      validator: (v) => v >= 0 && v <= 1,
    },
    delay: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      visible: false,
      positionClass: '',
      position: {
        left: 0,
        top: 0,
      },
      runner: null,
    };
  },
  computed: {
    showEventName() {
      return `show:${this.event}`;
    },
    hideEventName() {
      return `hide:${this.event}`;
    },
    className() {
      return ['vue-popover', this.pointer && this.positionClass];
    },
    style() {
      return {
        width: `${this.width}${typeof this.width == 'number' ? 'px' : ''}`,
        ...this.position,
      };
    },
  },
  mounted() {
    emitter.on(this.showEventName, this.showEventListener);
    emitter.on(this.hideEventName, this.hideEventListener);
  },
  unmounted() {
    emitter.off(this.showEventName, this.showEventListener);
    emitter.off(this.hideEventName, this.hideEventListener);
  },
  methods: {
    showEventListener(event) {
      if (this.visible) {
        emitter.emit(this.hideEventName);
        return;
      }
      this.$nextTick(() => {
        const { target, name, position } = event;
        window.clearTimeout(this.runner);
        if (name === this.name) {
          this.runner = window.setTimeout(() => {
            const direction = directions[position];
            this.positionClass = `dropdown-position-${position}`;
            this.visible = true;
            this.$nextTick(() => {
              this.$emit('show', event);
              this.$nextTick(() => {
                const position = this.getDropdownPosition(target, this.$refs.dropdown, direction);
                if (this.top) {
                  position.top += this.top;
                }

                this.position = {
                  left: `${position.left}px`,
                  top: `${position.top}px`,
                };
              });
            });
          }, this.delay);
        }
      });
    },
    hideEventListener(event) {
      window.clearTimeout(this.runner);
      if (this.visible) {
        this.visible = false;
        this.$emit('hide', event);
      }
    },
    getDropdownPosition(target, dropdown, direction) {
      if (!dropdown)
        return {
          left: 0,
          top: 0,
        };
      const trRect = target.getBoundingClientRect();
      const ddRect = dropdown.getBoundingClientRect();
      // Scroll offset of the current document
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft;
      // Position within the parent
      const offsetLeft = trRect.left + scrollLeft;
      const offsetTop = trRect.top + scrollTop;
      // const shiftX = ddRect.width - trRect.width
      const shiftY = 0.5 * (ddRect.height + trRect.height);
      // Center of the target element
      const centerX = offsetLeft - 0.5 * (ddRect.width - trRect.width);
      const centerY = offsetTop + trRect.height - shiftY;
      // let anchorX = direction[0] * this.anchor
      // let anchorY = direction[0] * this.anchor
      // Position of the dropdown relatively to target
      let x = direction[0] * 0.5 * (ddRect.width + trRect.width);
      let y = direction[1] * shiftY;
      // Pointer size correction
      if (this.pointer) {
        x += direction[0] * pointerSize;
        y += direction[1] * pointerSize;
      }
      return {
        left: centerX + x,
        top: centerY - y,
      };
    },
  },

  render() {
    if (!this.visible) {
      return;
    }

    return h(
      'div',
      {
        class: this.className,
        style: this.style,
        'data-popover': this.name,
        onClick(event) {
          event.stopPropagation();
        },
        ref: 'dropdown',
      },
      this.$slots.default(),
    );
  },
};
</script>

<style lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
$pointer-size: 4px;
$popper-background-color: #525252 !default;

.vue-popover {
  display: block;
  position: absolute;
  background: #fff;
  // box-shadow: 0px 4px 20px 0px rgba(52, 73, 94, 0.2);
  padding: 3px 6px;
  border-radius: $radius;
  transition: opacity 0.5s ease;

  z-index: 999999999999999999;
  &:before {
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    content: '';
  }
  &[data-popover='tooltip'] {
    max-width: 240px;
    color: #fff;
    padding: 6px 10px;
    font-size: 12px;
    line-height: 18px;
    background: $popper-background-color;
    border-radius: $radius;
    box-shadow: 2px 2px 3px rgba(#000, 0.4);
    box-sizing: border-box;
    &.dropdown-position-bottom:before {
      border-bottom: $pointer-size solid $dark1;
    }
    &.dropdown-position-bottomsidebar {
      margin-top: 8px;
      &:before {
        border-bottom: $pointer-size solid $dark1;
      }
    }
    &.dropdown-position-top:before {
      border-top: $pointer-size solid $dark1;
    }
    &.dropdown-position-left:before {
      border-left: $pointer-size solid $dark1;
    }
    &.dropdown-position-right:before {
      border-right: $pointer-size solid $dark1;
    }
  }
  &.dropdown-position-bottom:before,
  &.dropdown-position-bottomsidebar:before {
    border-left: $pointer-size solid transparent;
    border-right: $pointer-size solid transparent;
    border-bottom: $pointer-size solid $dark1;
    top: -$pointer-size;
    left: calc(50% - #{$pointer-size});
    // filter: drop-shadow(0px -2px 2px rgba(52, 73, 94, 0.1));
  }
  &.dropdown-position-top:before {
    border-left: $pointer-size solid transparent;
    border-right: $pointer-size solid transparent;
    border-top: $pointer-size solid $dark1;
    bottom: -$pointer-size;
    left: calc(50% - #{$pointer-size});
    // filter: drop-shadow(0px 2px 2px rgba(52, 73, 94, 0.1));
  }
  &.dropdown-position-left:before {
    border-top: $pointer-size solid transparent;
    border-bottom: $pointer-size solid transparent;
    border-left: $pointer-size solid $dark1;
    right: -$pointer-size;
    top: calc(50% - #{$pointer-size});
    // filter: drop-shadow(2px 0px 2px rgba(52, 73, 94, 0.1));
  }
  &.dropdown-position-right:before {
    border-top: $pointer-size solid transparent;
    border-bottom: $pointer-size solid transparent;
    border-right: $pointer-size solid $dark1;
    left: -$pointer-size;
    top: calc(50% - #{$pointer-size});
    // filter: drop-shadow(-2px 0px 2px rgba(52, 73, 94, 0.1));
  }
}
</style>
